import Box from '@mui/material/Box';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isStatesAndProvinces } from 'api/responses/models/Configuration/StatesAndProvincesDto';
import { ControlledFormattedTextField } from 'library/components/controls/form/ControlledFormattedTextField';
import { ControlledTextField } from 'library/components/controls/form/ControlledTextField';
import { NUMBER_MASK } from 'library/constants/InputMasks';
import { SxProps } from 'library/types';
import { emailPattern } from 'library/validation';

import { ECMCountryInput } from './ECMCountryInput';
import { NewCustomerConsents } from './NewCustomerConsents';
import { NotificationLanguageSelect } from './NotificationLanguageSelect';
import { PhonePrefixInput } from './PhonePrefixInput';
import {
	StatesAndProvincesInput,
	StatesAndProvincesInputCustomProps,
} from './StatesAndProvincesInput';
import {
	EndCustomerWithLanguageDto,
	MARKETING_CONSENT_LEGAL_REFERENCE,
	STORAGE_CONSENT_LEGAL_REFERENCE,
} from './types';

// all fields must be set to empty, so all fields will be cleared
export const emptyCustomer: Partial<Omit<EndCustomerWithLanguageDto, 'id'>> = {
	email: '',
	companyName: '',
	vatNumber: '',
	phoneNumber: '',
	firstname: '',
	surname: '',
	countryCode: '',
	county: '',
	city: '',
	postalCode: '',
	addressFirstPart: '',
	addressSecondPart: '',
	title: '',
	consentApprovals: [],
};

type NewCustomerFormProps = {
	idPrefix?: string;
	withNotificationLanguage?: boolean; // is valid only for adding a customer to a unit
};

export const NewCustomerForm = ({
	idPrefix = 'modification',
	withNotificationLanguage = true,
}: NewCustomerFormProps) => {
	const { t } = useTranslation(['common', 'commonButton', 'customer']);
	const { control, watch } = useFormContext<EndCustomerWithLanguageDto>();

	const watchConsentApprovals = watch('consentApprovals');
	const isStorageConsentGiven = watchConsentApprovals?.find(
		({ legalReference }) =>
			legalReference === STORAGE_CONSENT_LEGAL_REFERENCE
	)?.value;
	const disabledConsents = useMemo(
		() =>
			isStorageConsentGiven === true
				? []
				: [MARKETING_CONSENT_LEGAL_REFERENCE],
		[isStorageConsentGiven]
	);
	const displaySxOnNoConsentToStorage: SxProps = useMemo(
		() => (isStorageConsentGiven !== false ? {} : { display: 'none' }),
		[isStorageConsentGiven]
	);
	const isRequiredField = useMemo(
		() => isStorageConsentGiven !== false,
		[isStorageConsentGiven]
	);

	const country = watch('countryCode');

	const isDefinedStatesAndProvinces = isStatesAndProvinces(country);

	const StateInput = useMemo(
		() =>
			isDefinedStatesAndProvinces
				? StatesAndProvincesInput
				: ControlledTextField,
		[isDefinedStatesAndProvinces]
	);

	const additionalProps = useMemo(
		() =>
			isDefinedStatesAndProvinces
				? {
						countryCodeWithStatesAndProvinces: country,
					}
				: ({} as StatesAndProvincesInputCustomProps),
		[isDefinedStatesAndProvinces, country]
	);

	return (
		<>
			<Box
				sx={{
					mb: 2,
					display: 'grid',
					gridTemplateColumns:
						'auto repeat(2, minmax(min-content, 280px)) auto',
					'&>button:first-of-type': { gridColumn: '2' },
					'&>button:last-of-type': { gridColumn: '3' },
				}}></Box>
			<Box
				sx={{
					display: 'grid',

					gridTemplateColumns: {
						xs: 'repeat(1, 1fr)',
						sm: 'repeat(3, 1fr)',
					},

					gap: 2,
				}}>
				<ControlledTextField
					id={idPrefix + 'email-input'}
					label={t('common:email')}
					name="email"
					type="email"
					size="small"
					sx={displaySxOnNoConsentToStorage}
					control={control}
					validation={{
						pattern: emailPattern,
						required: isRequiredField,
					}}
				/>
				{isRequiredField ? (
					<>
						<ControlledTextField
							id={idPrefix + 'company-name-input'}
							label={t('common:company-name')}
							name="companyName"
							size="small"
							sx={displaySxOnNoConsentToStorage}
							control={control}
						/>
						<ControlledTextField
							id={idPrefix + 'vat-number-input'}
							label={t('common:vat-number')}
							name="vatNumber"
							size="small"
							sx={displaySxOnNoConsentToStorage}
						/>
					</>
				) : (
					<>
						<Box
							sx={{
								display: { xs: 'none', sm: 'block' },
							}}></Box>
						<Box
							sx={{
								display: { xs: 'none', sm: 'block' },
							}}></Box>
					</>
				)}
				<Box
					sx={{
						display: 'flex',
						gap: 2,
					}}>
					<PhonePrefixInput
						id={idPrefix + 'phone-prefix-input'}
						label={t('prefix')}
						name="phonePrefixCountryCode"
						size="small"
						control={control}
						sx={{ flex: 2, ...displaySxOnNoConsentToStorage }}
						validation={{ required: isRequiredField }}
					/>
					<ControlledFormattedTextField
						id={idPrefix + 'phone-number-input'}
						label={t('common:phone-number')}
						name="phoneNumber"
						type="tel"
						size="small"
						control={control}
						imaskProps={{
							mask: NUMBER_MASK,
							type: 'tel',
						}}
						sx={{ flex: 3, ...displaySxOnNoConsentToStorage }}
						validation={{ required: isRequiredField }}
					/>
				</Box>
				<ControlledTextField
					id={idPrefix + 'first-name-input'}
					label={t('common:first-name')}
					name="firstname"
					size="small"
					sx={displaySxOnNoConsentToStorage}
					control={control}
					validation={{ required: isRequiredField }}
				/>
				<ControlledTextField
					id={idPrefix + 'last-name-input'}
					label={t('common:last-name')}
					name="surname"
					size="small"
					sx={displaySxOnNoConsentToStorage}
					control={control}
					validation={{
						required: isRequiredField,
					}}
				/>
				<ECMCountryInput
					id={idPrefix + 'country-input'}
					label={t('common:country')}
					name="countryCode"
					size="small"
					control={control}
					validation={{ required: true }}
				/>
				<StateInput
					id={idPrefix + 'county-input'}
					label={t('common:county')}
					name="county"
					size="small"
					sx={displaySxOnNoConsentToStorage}
					control={control}
					{...additionalProps}
				/>
				<ControlledTextField
					id={idPrefix + 'city-input'}
					label={t('common:city')}
					name="city"
					size="small"
					control={control}
					validation={{ required: true }}
				/>
				<ControlledTextField
					id={idPrefix + 'zip-code-input'}
					label={t('common:zip-code')}
					name="postalCode"
					size="small"
					control={control}
					validation={{ required: true }}
				/>
				<ControlledTextField
					id={idPrefix + 'address-input'}
					label={t('common:address')}
					name="addressFirstPart"
					size="small"
					sx={{
						gridColumn: { sm: 'span 2' },
						...displaySxOnNoConsentToStorage,
					}}
					control={control}
					validation={{ required: isRequiredField }}
				/>
				<ControlledTextField
					id={idPrefix + 'address-input'}
					label={t('common:additional-address')}
					name="addressSecondPart"
					size="small"
					sx={{
						gridColumn: { sm: 'span 2' },
						...displaySxOnNoConsentToStorage,
					}}
					control={control}
				/>
			</Box>
			<Box
				sx={{
					display: 'grid',

					gridTemplateColumns: {
						xs: '2fr 1fr',
						sm: '3fr 1fr',
						md: '4fr 1fr',
					},

					gap: 4,
					my: 4,
				}}>
				<NewCustomerConsents disabled={disabledConsents} />
				{withNotificationLanguage && (
					<NotificationLanguageSelect
						id={idPrefix + 'notification-language-select'}
						label={t('customer:notification-language-label')}
						name="notificationLanguage"
						size="small"
						control={control}
						validation={{ required: true }}
						sx={{ gridColumn: 2 }}
					/>
				)}
			</Box>
		</>
	);
};
